import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import useParentMessage from "../../../utils/hooks/useParentMessage";
import useFacility from "../../../utils/hooks/useFacility";
import { SWIMMING_COURSE_TRANSACTION_URL } from "../../api/apiRoutes";
import { InputsFinalize } from "../FinalizePage/FinalizeNoLogin";
import { setSaveData } from "../../models/SaveData";
import { PURCHASE } from "../../routes";
import { State } from "../../store";
import useNewsletterConfirmation from "../NewsletterConfirmation/useNewsletterConfirmation";
import { sendCheckoutNewsletterInvite } from "../NewsletterConfirmation/sendCheckoutNewsletterInvite";

interface UseSwimmingCourseTransactionResult {
  saveTransaction: (formData: InputsFinalize) => Promise<boolean>;
  loading: boolean;
  error: string;
  paymentUrl: string;
}

const redirectToPayment = (paymentUrl: string, forceGoToPayment: (url: string) => void) => {
  if (!paymentUrl || paymentUrl.length < 4) {
    return;
  }

  forceGoToPayment(paymentUrl);

  // Poza iframe parent nie obsługuje postMessage — przekieruj bezpośrednio
  if (window.self === window.top) {
    window.location.href = paymentUrl;
  }
};

export default function useSwimmingCourseTransaction(): UseSwimmingCourseTransactionResult {
  const [searchParams] = useSearchParams();
  const { post } = useApiRequest();
  const { forceGoToPayment } = useParentMessage();
  const { sendInvite } = useNewsletterConfirmation();
  const facilityIds = useFacility();
  const alreadySubscribed = useSelector((state: State) => state.auth.userData.allowMailing);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentUrl, setPaymentUrl] = useState("");

  const saveTransaction = async (formData: InputsFinalize): Promise<boolean> => {
    const a = searchParams.get("a") ?? "";
    const objectIds = searchParams.get("id") ?? "";

    setLoading(true);
    setError("");
    setPaymentUrl("");

    const { result, isError, errorTable } = await post(SWIMMING_COURSE_TRANSACTION_URL(objectIds), {
      ...setSaveData(formData),
      a,
    });

    if (isError) {
      setError(errorTable?.[0] ?? "Wystąpił błąd podczas przetwarzania płatności. Spróbuj ponownie.");
      setLoading(false);
      return false;
    }

    const resultPaymentUrl: string = result?.PaymentURL ?? "";
    setPaymentUrl(resultPaymentUrl);

    await sendCheckoutNewsletterInvite({
      sendInvite,
      facilityIds,
      email: formData.email,
      allowMailing: formData.allowMailing,
      alreadySubscribed,
    });

    setLoading(false);

    if (resultPaymentUrl.length >= 4) {
      redirectToPayment(resultPaymentUrl, forceGoToPayment);
    } else {
      setTimeout(() => {
        navigate(PURCHASE);
      }, 1500);
    }

    return true;
  };

  return { saveTransaction, loading, error, paymentUrl };
}
